import React from "react";
import classNames from "classnames";
import StyleInjector from "../../../../layout/StyleInjector";
import {FieldProps, OrderMeta} from "../Types/FieldProps";
import {RequestParams} from "../Types/RequestParams";
import {ColumnStateType} from "../Types/ColumnStateType";
import {OrderType} from "../Types/OrderType";
import Filter from "./Filter";
import FilterComp from "./FilterComp";
import DefaultFilter from "./DefaultFilter";
import PropsProxy from "../../../../layout/PropsProxy";
import {Tooltip} from "react-tippy";

type ColumnProps = FieldProps & {
    calculatedColumnWidth: undefined|number,
    data: ResponseData,
    loadData: (request: RequestParams) => Promise<ResponseData>,
    request: RequestParams,
    state: ColumnStateType
    changeOrder: (order: OrderType|null|undefined, meta: any, cb?: Function) => void,
    setState: (state: ColumnStateType, cb?: Function) => void,
}

let columnId = 0;
const getId = () => {
    columnId++;
    if (Number.MAX_SAFE_INTEGER - 10 < columnId) {
        columnId = 1;
    }
    return columnId;
}

export default class Column extends React.Component<ColumnProps, {}> {

    state = {
        id: "table_head_" + getId()
    }

    constructor(props) {
        super(props);
        this.changeOrder = this.changeOrder.bind(this);
        this.getField = this.getField.bind(this);
        this.getFilter = this.getFilter.bind(this);
    }

    changeOrder({event, direction = null} = {}) {
        const field = this.getField();
        const defaultSort = this.props.defaultSort || 1;
        let currentDirection: "ASC"|"DESC" = (defaultSort === "DESC" || defaultSort === 1) ? "DESC" : "ASC";
        let order: OrderType = this.props.state.order ? {...(this.props.state.order)} : null;
        if (direction === order?.direction) {
            direction = false
        }
        order = order || {field, direction: currentDirection};
        if (direction || direction === false) {
            order = {
                field,
                direction,
            };
        } else {
            if (!this.props.state.order) {
                order = {
                    field,
                    direction: currentDirection,
                };
            } else {
                let orderList = [currentDirection, currentDirection === "DESC" ? "ASC" : "DESC", null];
                order.direction = orderList[orderList.indexOf(order.direction) + 1];
            }
        }
        if (!order.direction) {
            order = undefined;
        }
        const meta: OrderMeta = {exclusive: !event.ctrlKey && !event.shiftKey};
        if (this.props.sortFunction) {
            order = this.props.sortFunction(order, meta);
        }
        this.props.changeOrder(order, meta);
    }

    getField() {
        return this.props.field;
    }

    getFilter() {
        let filter = React.Children.map(this.props.children || [], (child: React.ReactElement, idx) => {
            if (!child) return;
            if (child?.type?.prototype instanceof Filter || child.type === Filter) {
                return <child.type
                    key={idx}
                    {...child.props}
                    {...this.props}
                />;
            }
        }).filter(Boolean);
        if (!filter.length) {
            if (this.props.filterable === true) {
                filter = [<DefaultFilter
                    key={0}
                    {...this.props}
                />];
            } else if (this.props.filterable) {
                let FilterComp = this.props.filterable;
                if (FilterComp instanceof Function) {
                    FilterComp = <FilterComp key={0} {...this.props} />;
                } else if (FilterComp.type) {
                    FilterComp = <FilterComp.type key={0} ref={FilterComp.ref} {...this.props} {...FilterComp.props} />;
                } else {
                    FilterComp = <FilterComp key={0} {...this.props} />;
                }
                filter = [FilterComp];
            }
        }
        return filter;
    }

    render() {
        const field = this.getField();
        const order = this.props.state.order;
        const filter = this.getFilter();

        let column = this.props?.data?.columns?.[field]?.label;
        let label = column === field ? null : column;
        let style = this.props.style;
        if (style instanceof Function) {
            style = style(this.props);
        }

        let LabelHolder: any = "span";
        let labelProps: {html?: any, className: any} = {className: "column-label"};

        const t = label || this.props.header || column || field;
        if (typeof t !== "object" && typeof t !== "function") {
            LabelHolder = Tooltip;
            labelProps.html = t;
            labelProps.className += " column-scalar";
        } else {
            labelProps.className += " column-object";
        }

        return (
            <th
                style={style}
                className={classNames(this.props.className, "p-editable-column p-cell-editing p-resizable-column " + this.state.id, {"p-sortable-column": this.props.sortable})}
            >
                {this.props.calculatedColumnWidth ? <StyleInjector
                    id={this.state.id}
                    style={`
                        width: ${this.props.calculatedColumnWidth}% !important;
                    `}
                /> : null}
                <PropsProxy>
                    {this.props.actions}
                </PropsProxy>
                <div
                    className="p-cell-header"
                    onClick={(event) => {
                        event && event.preventDefault();
                        event && event.stopPropagation();
                        this.props.sortable && this.changeOrder({event})
                    }}
                >
                    <LabelHolder {...labelProps}>
                        <PropsProxy {...this.props}>
                            {t}
                        </PropsProxy>
                    </LabelHolder>
                    {
                        this.props.sortable &&
                        <span className="p-sortable-column-sorter">
                            <span
                                className={classNames("p-sortable-column-icon pi pi-fw pi-sort-up", {
                                    "p-highlight": order?.direction === "ASC"
                                })}
                                onClick={(event) => {
                                    event && event.preventDefault();
                                    event && event.stopPropagation();
                                    this.props.sortable && this.changeOrder({event, direction: "ASC"})
                                }}
                            />
                            {/*order?.direction && <span
                                className={classNames("p-sortable-column-icon pi pi-fw pi-circle-on")}
                                onClick={(event) => {
                                    event && event.preventDefault();
                                    event && event.stopPropagation();
                                    this.props.sortable && this.changeOrder({event, direction: null})
                                }}
                            />*/}
                            <span
                                className={classNames("p-sortable-column-icon pi pi-fw pi-sort-down", {
                                    "p-highlight": order?.direction === "DESC"
                                })}
                                onClick={(event) => {
                                    event && event.preventDefault();
                                    event && event.stopPropagation();
                                    this.props.sortable && this.changeOrder({event, direction: "DESC"})
                                }}
                            />
                        </span>
                    }
                </div>
                {!!filter.length && <FilterComp
                    {...this.props}
                    filter={this.props.state.filter}
                    filterOpened={this.props.state.filterOpened}
                    setFilter={(filter) => {
                        if (filter) {
                            filter = {...filter};
                        }
                        this.props.setState({filter, filterOpened: true});
                    }}
                    setFilterOpened={(filterOpened: boolean) => {
                        let state: ColumnStateType = {filterOpened};
                        if (!filterOpened) {
                            state.filter = undefined;
                        }
                        this.props.setState(state);
                    }}
                >
                    {filter}
                </FilterComp>}
            </th>
        );
    }
}